import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import { Chip, Link } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { black, blackText, blueGrayText } from '../../constants/Colors';
import { screenType, status } from '../../constants/enum';

import BackgroundEditModal from './BackgroundEditModal';

export interface RegisterInfoProps {
  screen: number;
  face: string;
  faceUrl: string;
  backImage: string;
  companyName?: string;
  companyUrl?: string;
  statusNum: number;
  desc?: string;
  table: {
    aboveType?: string;
    aboveDesc?: string;
    middleType?: string;
    middleDesc?: string;
    bottomType?: string;
    bottomDesc?: Array<{
      productNames: string | null;
      productUrls: string | null;
    }>;
  };
}

const styles = {
  avatar: {
    '@media screen and (max-width:480px)': {
      height: '80px',
      marginTop: '-30px',
      width: '80px'
    },
    border: '1px solid #FFFFFF',
    height: '110px',
    marginTop: '-55px',
    width: '110px'
  },
  avatarMl: {
    '@media screen and (max-width:730px)': {
      flexDirection: 'column',
      ml: 0,
      mt: '8px',
      rowGap: '12px'
    },
    columnGap: '12px',
    flexDirection: 'row',
    ml: '24px',
    width: 'unset'
  },
  button: {
    '&:hover': {
      backgroundColor: '#000000',
      color: '#fff',
      opacity: 0.35
    },
    backgroundColor: '#000000',
    color: '#fff',
    opacity: 0.5
  },
  buttonContainerPc: {
    '@media screen and (max-width:750px)': { display: 'none' }
  },
  buttonContainerSp: {
    '@media screen and (min-width:751px)': { display: 'none' }
  },
  buttonFlexGrow: {
    flexGrow: 1
  },
  buttonFont: {
    fontSize: '13px'
  },
  buttonLocation: {
    '@media screen and (max-width:480px)': {
      right: '8px',
      top: '8px'
    },
    position: 'absolute',
    right: '16px',
    top: '16px'
  },
  card: {
    '@media screen and (max-width:480px)': { margin: '16px' },
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px',
    position: 'relative'
  },
  companyName: {
    '@media screen and (max-width:480px)': {
      fontSize: '16px',
      width: '100%',
      wordBreak: 'break-all'
    },
    color: black,
    fontSize: '20px',
    fontWeight: 700
  },
  content: {
    '&:last-child': {
      paddingBottom: '40px'
    },
    '@media screen and (max-width:480px)': { padding: '16px' },
    padding: '16px 40px 40px 40px',
    textAlign: 'left'
  },
  description: {
    '@media screen and (max-width:480px)': { fontSize: '12px' },
    color: black,
    fontSize: '14px',
    marginTop: '22px'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black,
    marginBottom: '24px',
    marginTop: '24px'
  },
  edit: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  editButton: {
    backGroundColor: 'white',
    border: `1px solid ${black}`,
    color: blackText,
    maxWidth: '142px',
    width: '100%'
  },
  fontMd: {
    color: black,
    fontSize: '14px'
  },
  fontSm: {
    fontSize: '12px'
  },
  heading: {
    fontWeight: 'bold'
  },
  media: {
    '@media screen and (max-width:480px)': { height: '85.75px' },
    height: '284px',
    marginTop: '30'
  },
  subheading: {
    lineHeight: 1.8
  },
  tag: {
    '.MuiChip-label': {
      padding: '3px 16px'
    },
    backgroundColor: '#F3F3F3',
    border: '1px solid #757575',
    borderRadius: '4px',
    color: '#757575',
    fontSize: '12px',
    height: '24px',
    mt: '4px',
    width: '95px'
  },
  textMt: {
    marginTop: '16px'
  }
};

export default function RegisterInfo(props: RegisterInfoProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    face,
    faceUrl,
    backImage,
    companyName,
    companyUrl,
    statusNum,
    desc,
    table,
    screen
  } = props;

  const [open, setOpen] = useState(false);
  const [defaultImage, setDefaultImage] = useState<string>('');
  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);

  const transitionEditPage = (): void => {
    switch (screen) {
      case screenType.client:
        navigate('/client/mypage/company/edit');
        break;
      case screenType.agent:
        navigate('/agent/mypage/company/edit');
        break;
      case screenType.publisher:
        navigate('/publisher/mypage/company/edit');
        break;
      default:
        break;
    }
  };

  return (
    <>
      <BackgroundEditModal
        open={open}
        handleOpen={handleOpen}
        handleClose={handleClose}
        setImage={setDefaultImage}
      />
      <Grid container>
        <Grid item xs={12}>
          <Card sx={styles.card}>
            <CardMedia
              component="img"
              image={defaultImage || backImage}
              sx={styles.media}
            />

            <CardContent sx={styles.content}>
              <Grid
                container
                sx={{
                  '@media screen and (max-width:730px)': {
                    flexDirection: 'column'
                  },
                  flexDirection: 'row'
                }}
              >
                <Grid
                  sx={{
                    '@media screen and (max-width:750px)': {
                      display: 'flex',
                      flexDirection: 'row'
                    }
                  }}
                >
                  <Avatar sx={styles.avatar} key={face} src={faceUrl} />
                  {/* SP用の編集ボタン */}
                  <Grid sx={[styles.buttonFlexGrow, styles.buttonContainerSp]}>
                    <Typography sx={styles.edit}>
                      <Button
                        startIcon={<EditIcon />}
                        sx={styles.editButton}
                        onClick={transitionEditPage}
                      >
                        {t('mypage.infoEdit')}
                      </Button>
                    </Typography>
                  </Grid>
                </Grid>

                <Grid
                  container
                  flexDirection={'row'}
                  columnGap={'12px'}
                  sx={styles.avatarMl}
                >
                  <Grid item flex={1} container flexDirection={'column'}>
                    <Typography sx={styles.companyName} component="div">
                      {companyName}
                    </Typography>
                    <Link
                      href={companyUrl}
                      sx={{
                        '&:hover': {
                          textDecoration: 'underline',
                          textDecorationColor: black
                        },
                        color: black,
                        textDecoration: 'none'
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ color: '#333333' }}
                        {...styles.fontSm}
                      >
                        {companyUrl}
                      </Typography>
                    </Link>
                  </Grid>
                  <Chip
                    label={
                      statusNum === status.approved
                        ? t('status.approved')
                        : statusNum === status.notChecked
                        ? t('status.notChecked')
                        : statusNum === status.fix
                        ? t('status.fix')
                        : t('status.deleted')
                    }
                    sx={styles.tag}
                  />
                </Grid>

                {/* PC用の編集ボタン */}
                <Grid sx={[styles.buttonFlexGrow, styles.buttonContainerPc]}>
                  <Typography sx={styles.edit}>
                    <Button
                      startIcon={<EditIcon />}
                      sx={styles.editButton}
                      onClick={transitionEditPage}
                    >
                      {t('mypage.infoEdit')}
                    </Button>
                  </Typography>
                </Grid>
              </Grid>
              <Typography component="p" sx={styles.description}>
                {desc}
              </Typography>
              <Divider light sx={styles.divider} />
              <Grid container>
                <Grid md={3} xs={12} item>
                  <Typography
                    component="p"
                    sx={blueGrayText}
                    {...styles.fontMd}
                  >
                    {table.aboveType}
                  </Typography>
                </Grid>
                <Grid md={9} xs={12} item>
                  <Typography component="p" sx={styles.fontMd}>
                    {table.aboveDesc}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container sx={{ marginTop: '16px' }}>
                <Grid md={3} xs={12} item>
                  <Typography
                    component="p"
                    sx={blueGrayText}
                    {...styles.fontMd}
                  >
                    {table.middleType}
                  </Typography>
                </Grid>
                <Grid md={9} xs={12} item>
                  <Typography component="p" sx={styles.fontMd}>
                    {table.middleDesc}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container sx={styles.textMt}>
                <Grid md={3} xs={12} item>
                  <Typography
                    component="p"
                    sx={blueGrayText}
                    {...styles.fontMd}
                  >
                    {table.bottomType}
                  </Typography>
                </Grid>
                <Grid md={9} xs={12} item>
                  {table.bottomDesc?.map(
                    (
                      element: {
                        productNames: string | null;
                        productUrls: string | null;
                      },
                      index: number
                    ) => (
                      <Typography component="div" key={index}>
                        {element?.productUrls && (
                          <Link
                            href={element?.productUrls}
                            key={index}
                            sx={{ color: black, textDecorationColor: black }}
                          >
                            <Typography component="div" sx={styles.fontMd}>
                              {element?.productNames
                                ? element?.productNames
                                : element?.productUrls}
                            </Typography>
                          </Link>
                        )}
                        {!element?.productUrls && (
                          <Typography component="div" sx={styles.fontMd}>
                            {element?.productNames ? element?.productNames : ''}
                          </Typography>
                        )}
                      </Typography>
                    )
                  )}
                  <Typography
                    component="p"
                    sx={[styles.fontMd, { whiteSpace: 'pre-line' }]}
                  ></Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
